import type { DealServiceFormData } from '../types';

export interface ServiceValidationError {
  index: number;
  label: string;
  missing: string[];
}

/**
 * Valida i serviceItems di un'offerta prima del submit.
 *
 * Per ogni servizio incompleto restituisce l'indice, un'etichetta
 * utente-friendly e l'elenco dei campi mancanti (in italiano).
 * Le righe prodotto magazzino (line_type='product') hanno regole
 * dedicate: niente payment_method richiesto, ma quantity + unit_price
 * sono obbligatori al posto di amount.
 *
 * Pura: nessun side-effect, nessun toast. Il caller decide come
 * mostrare gli errori e dove saltare.
 */
export function validateDealServices(
  serviceItems: DealServiceFormData[]
): ServiceValidationError[] {
  const errors: ServiceValidationError[] = [];

  serviceItems.forEach((service, index) => {
    const isCustomService = service.service_id?.toString().startsWith('custom-');
    const isProduct = (service as any).line_type === 'product';

    const label =
      service.service?.title ||
      service.custom_service_name ||
      (isProduct ? `Prodotto #${index + 1}` : `Servizio #${index + 1}`);

    const missing: string[] = [];

    // Selezione servizio / nome personalizzato
    if (isCustomService) {
      const hasValidName = !!service.custom_service_name || (service.service && !!service.service.title);
      if (!hasValidName) missing.push('nome');
    } else if (!service.service_id) {
      missing.push('selezione servizio');
    }

    // Metodo di pagamento o rate personalizzate (solo servizi, non prodotti)
    const hasCustomSchedule =
      !service.payment_method_id &&
      Array.isArray((service.payment_schedule as any)?.installments) &&
      (service.payment_schedule as any).installments.length > 0;

    if (!isProduct && !service.payment_method_id && !hasCustomSchedule) {
      missing.push('metodo di pagamento o rate personalizzate');
    }

    if (!isProduct && hasCustomSchedule) {
      const installments = (service.payment_schedule as any).installments as Array<{ amount?: number }>;
      const totalScheduled = installments.reduce(
        (sum, inst) => sum + (Number.isFinite(inst.amount) ? Number(inst.amount) : 0),
        0
      );
      const expected = service.discounted_amount ?? service.amount ?? 0;
      if (Math.abs(totalScheduled - expected) > 0.01) {
        missing.push(`rate personalizzate non coincidono (€${totalScheduled.toFixed(2)} vs €${expected.toFixed(2)})`);
      }
    }

    // Quantity/unit_price (prodotti) vs amount (servizi)
    if (isProduct) {
      if (!(service as any).quantity || (service as any).quantity <= 0) missing.push('quantità');
      if ((service as any).unit_price == null || (service as any).unit_price < 0) missing.push('prezzo unitario');
    } else {
      const hasValidAmount =
        service.amount >= 0 ||
        (service.discounted_amount !== undefined && service.discounted_amount >= 0);
      if (!hasValidAmount) missing.push('importo');
    }

    if (missing.length > 0) {
      errors.push({ index, label, missing });
    }
  });

  return errors;
}

/**
 * Formatta un array di ServiceValidationError in un singolo messaggio
 * toast multi-riga, scegliendo IT/EN in base al lang.
 */
export function formatValidationErrorMessage(
  errors: ServiceValidationError[],
  language: string
): string {
  const lines = errors.map(e => `• ${e.label}: manca ${e.missing.join(', ')}`);
  return language === 'it'
    ? `Servizi incompleti — completa i campi o rimuovi le righe:\n${lines.join('\n')}`
    : `Incomplete services — fill in or remove rows:\n${lines.join('\n')}`;
}
